import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { mealDetails } from '../../mocks/mealDetails';
import { meals } from '../../mocks/meals';
import MealGallery from './components/MealGallery';
import NutritionCard from './components/NutritionCard';
import ReviewsSection from './components/ReviewsSection';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function MealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<'ingredients' | 'allergens'>('ingredients');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const mealId = Number(id);
  const meal = mealDetails[mealId];

  if (!meal) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <i className="ri-bowl-line text-6xl text-gray-300 mb-4"></i>
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Meal not found</h2>
        <p className="text-gray-500 mb-6">This meal doesn't exist or has been removed.</p>
        <Link
          to="/"
          className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
        >
          Back to Menu
        </Link>
      </div>
    );
  }

  const relatedMeals = meals.filter((m) => m.id !== mealId).slice(0, 3);

  const handleAddToCart = () => {
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-primary-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/#menu" className="hover:text-primary-500 transition-colors cursor-pointer">Menu</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium truncate max-w-xs">{meal.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Gallery */}
          <div>
            <MealGallery
              mainImage={meal.image}
              galleryImages={meal.galleryImages}
              name={meal.name}
            />
          </div>

          {/* Right: Info */}
          <div className="flex flex-col gap-6">
            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {meal.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-primary-100 text-primary-600 text-xs font-semibold rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <div>
              <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-3">{meal.name}</h1>
              <p className="text-gray-600 text-lg leading-relaxed">{meal.longDescription}</p>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-6 py-4 border-y border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-fire-line text-primary-500"></i>
                </div>
                <span><strong className="text-gray-900">{meal.calories}</strong> kcal</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-time-line text-primary-500"></i>
                </div>
                <span><strong className="text-gray-900">{meal.prepTime}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-user-line text-primary-500"></i>
                </div>
                <span><strong className="text-gray-900">{meal.servingSize}</strong></span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-star-fill text-primary-500"></i>
                </div>
                <span><strong className="text-gray-900">{meal.rating}</strong> ({meal.reviewCount})</span>
              </div>
            </div>

            {/* Chef */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary-200 flex-shrink-0">
                <img src={meal.chefAvatar} alt={meal.chef} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Prepared by</p>
                <p className="font-semibold text-gray-900">{meal.chef}</p>
              </div>
            </div>

            {/* Price & Quantity */}
            <div className="flex items-center gap-6">
              <span className="text-4xl font-bold text-primary-500">€{meal.price.toFixed(2)}</span>
              <div className="flex items-center gap-3 bg-gray-100 rounded-full px-2 py-1">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white transition-all cursor-pointer"
                >
                  <i className="ri-subtract-line text-gray-700"></i>
                </button>
                <span className="w-6 text-center font-bold text-gray-900">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white transition-all cursor-pointer"
                >
                  <i className="ri-add-line text-gray-700"></i>
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              className={`w-full py-4 rounded-full text-white font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 ${
                added ? 'bg-green-500' : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              <i className={`text-xl ${added ? 'ri-check-line' : 'ri-shopping-cart-line'}`}></i>
              {added ? 'Added to Cart!' : `Add to Cart — €${(meal.price * quantity).toFixed(2)}`}
            </button>

            {/* Ingredients / Allergens Tabs */}
            <div className="bg-gray-50 rounded-2xl p-5">
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setActiveTab('ingredients')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === 'ingredients'
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Ingredients
                </button>
                <button
                  onClick={() => setActiveTab('allergens')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === 'allergens'
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Allergens
                </button>
              </div>

              {activeTab === 'ingredients' && (
                <div className="flex flex-wrap gap-2">
                  {meal.ingredients.map((ing) => (
                    <span key={ing} className="px-3 py-1 bg-white border border-gray-200 text-gray-700 text-xs rounded-full shadow-sm">
                      {ing}
                    </span>
                  ))}
                </div>
              )}

              {activeTab === 'allergens' && (
                <div>
                  {meal.allergens.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {meal.allergens.map((a) => (
                        <span key={a} className="px-3 py-1 bg-red-50 border border-red-200 text-red-600 text-xs font-medium rounded-full flex items-center gap-1">
                          <i className="ri-alert-line"></i>
                          {a}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-green-600 text-sm flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-lg"></i>
                      No common allergens
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Nutrition */}
        <div className="mt-16">
          <NutritionCard nutrition={meal.nutrition} />
        </div>

        {/* Reviews */}
        <ReviewsSection
          reviews={meal.reviews}
          rating={meal.rating}
          reviewCount={meal.reviewCount}
        />

        {/* Related Meals */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">You Might Also Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedMeals.map((related) => (
              <div
                key={related.id}
                onClick={() => navigate(`/meal/${related.id}`)}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={related.image}
                    alt={related.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex gap-1 flex-wrap">
                    {related.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-white/90 text-xs font-semibold text-gray-700 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 mb-1">{related.name}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-3">{related.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500 font-bold text-lg">€{related.price.toFixed(2)}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-fire-line text-primary-400"></i>
                      {related.calories} kcal
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
